import { FastshiftExpectedError } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { checkFeatureFlagGate } from '@wac-web/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@wac-web/utils/feature-flags/feature-flag-manifest';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';

import { FourOhFourDisplay } from '@/common/displays';
import { FastshiftPageBreadcrumbs } from '@/common/fastshift-page-breadcrumbs/fastshift-page-breadcrumbs';
import { useRoutes } from '@/common/hooks';
import { getOrganizationDisplayName } from '@/common/organizations-flyout/src/utils';
import { useFastshiftSelector } from '@/state';

import { useOrganizationDetailsPageAnalytics } from './hooks/use-organization-details-page-analytics';
import { messages } from './messages';
import { MigrationPlansTable } from './migration-plans-table/migration-plans-table';
import { OrganizationDcInstancesTable } from './organization-dc-instances-table/organization-dc-instances-table';
import { OrganizationSummary } from './summary/organization-summary';

const styles = cssMap({
	tabContent: {
		paddingTop: token('space.100'),
		flexGrow: 1,
		minWidth: 0,
	},
});

export const FastshiftOrganizationDetailsPage = () => {
	useOrganizationDetailsPageAnalytics();
	const { formatMessage } = useIntl();
	const { paths } = useRoutes();

	const { domainId, organizationId } = useParams<{
		domainId: string;
		organizationId: string;
	}>();
	const organizationDetail = useFastshiftSelector((state) => state.organizationDetail);

	if (domainId === undefined || organizationId === undefined) {
		return <FourOhFourDisplay />;
	}

	const isMigrationPlansEnabled = checkFeatureFlagGate(featureFlags.sxpFspMigrationPlans);

	const hasMatchingOrganizationDetail =
		organizationDetail.domainId === domainId && organizationDetail.orgId === organizationId;
	const organizationSummary = hasMatchingOrganizationDetail
		? organizationDetail.summary.data
		: null;
	const isOrganizationNameLoading =
		!hasMatchingOrganizationDetail || organizationDetail.summary.pending;
	const organizationName = getOrganizationDisplayName(
		{
			orgId: organizationId,
			orgName: organizationSummary?.orgName ?? null,
		},
		{ formatMessage },
	);
	const isNotFound =
		hasMatchingOrganizationDetail &&
		(organizationDetail.summary.error === FastshiftExpectedError.NOT_FOUND ||
			organizationDetail.dcInstances.error === FastshiftExpectedError.NOT_FOUND);

	if (isNotFound) {
		return <FourOhFourDisplay />;
	}

	return (
		<Stack space="space.300">
			<FastshiftPageBreadcrumbs
				isLoading={isOrganizationNameLoading}
				items={[
					{
						href: paths.domainScopedOrganizations ?? '',
						text: formatMessage(messages.organizationsListBreadcrumb),
					},
					{
						href: paths.domainScopedOrganizationDetails ?? '',
						text: organizationName,
					},
				]}
				loadingLabel={formatMessage(messages.loadingLabel)}
				skeletonWidths={[100, 120]}
				testId="organization-breadcrumb-skeleton"
			/>
			<Stack space="space.150">
				{isOrganizationNameLoading ? (
					<Skeleton width="300px" height="32px" testId="organization-name-skeleton" />
				) : (
					<Heading size="large" as="h1">
						{organizationName}
					</Heading>
				)}
			</Stack>
			<OrganizationSummary domainId={domainId} orgId={organizationId} />
			{isMigrationPlansEnabled ? (
				<Tabs id="organization-details-tabs">
					<TabList>
						<Tab>{formatMessage(messages.dcInstancesTabLabel)}</Tab>
						<Tab>{formatMessage(messages.migrationPlansTabLabel)}</Tab>
					</TabList>
					<TabPanel>
						<Stack xcss={styles.tabContent}>
							<OrganizationDcInstancesTable domainId={domainId} orgId={organizationId} />
						</Stack>
					</TabPanel>
					<TabPanel>
						<Stack xcss={styles.tabContent}>
							{/* TODO: (follow-up) replace with migration plans from the store when the API is implemented https://hello.jira.atlassian.cloud/browse/SXP-5383 */}
							<MigrationPlansTable plans={[]} />
						</Stack>
					</TabPanel>
				</Tabs>
			) : (
				<OrganizationDcInstancesTable domainId={domainId} orgId={organizationId} />
			)}
		</Stack>
	);
};
